import { MapViewerService } from '@3ddv/ngx-dvm-internal';
import { AfterViewInit, Component, computed, effect, EffectRef, ElementRef, inject, Signal, ViewChild } from '@angular/core';
import { ButtonComponent } from '@components/shared/button/button.component';
import { APP_CONFIG } from '@configuration/configuration';
import { ButtonColors } from '@models/enums/components/Button/ButtonColor.enum';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { MapButtons } from '@models/types/components/MapButtons.type';
import { DvmService } from '@services/dvm.service';
import anime from 'animejs';

@Component({
  selector: 'app-map-control-buttons',
  standalone: true,
  imports: [
    ButtonComponent
  ],
  templateUrl: './map-control-buttons.component.html',
  styleUrl: './map-control-buttons.component.css'
})
export class MapControlButtonsComponent implements AfterViewInit {

  // VIEWCHILDS
  @ViewChild('upBtn', { read: ElementRef })
  public upBtn!: ElementRef<HTMLButtonElement>;

  @ViewChild('downBtn', { read: ElementRef })
  public downBtn!: ElementRef<HTMLButtonElement>;

  @ViewChild('leftBtn', { read: ElementRef })
  public leftBtn!: ElementRef<HTMLButtonElement>;

  @ViewChild('rightBtn', { read: ElementRef })
  public rightBtn!: ElementRef<HTMLButtonElement>;


  // SERVICES
  private readonly dvmService: DvmService = inject(DvmService);
  private readonly configuration: Signal<Configuration>  = inject(APP_CONFIG);
  private  readonly mapButtons: Signal<MapButtons> = computed((): MapButtons=> this.configuration().Ui.MapButtons);
    
  // GLOBAL STATE
  private readonly view: Signal<AppViews | null> = this.dvmService.view;

  // STATES
  public hideElement: boolean = true;
  public readonly showPanningButtonElements = computed(() => this.mapButtons().panning);
  
  
  // LIFECYCLE HOOKS
  public ngAfterViewInit(): void {
    this.initComponent();
  }

  // BUTTON STYLES
  public readonly color: ButtonColors = ButtonColors.Blur;
  public readonly type:  ButtonType   = ButtonType.Pill;


  // METHODS
  private initComponent(): void {

    const viewer: MapViewerService = this.dvmService.viewer;

    viewer.waitInitialize().subscribe((): void => {

      // BIND INTERFACE ACTIONS
      viewer.bindInterfaceAction(this.upBtn.nativeElement,    'move-up');
      viewer.bindInterfaceAction(this.downBtn.nativeElement,  'move-down');
      viewer.bindInterfaceAction(this.leftBtn.nativeElement,  'move-left');
      viewer.bindInterfaceAction(this.rightBtn.nativeElement, 'move-right');

      // GET ZOOM TRIGGER
      viewer.getObservable('zooming').subscribe((): void => this.onMapZoomChange());

    });

  }

  private onMapZoomChange(): void {
    
    const isMapZoomed: boolean = this.dvmService.viewer.scaling_factor > this.dvmService.viewer.min_scaling_factor;

    if(isMapZoomed === !this.hideElement) return;
    
    this.toggleButtons(isMapZoomed);

  }

  private toggleButtons(show: boolean): void {

    const elements: HTMLButtonElement[] = [
      this.upBtn.nativeElement,
      this.downBtn.nativeElement,
      this.leftBtn.nativeElement,
      this.rightBtn.nativeElement
    ];

    if(show) this.hideElement = false;

    anime({
      targets: elements,
      opacity: show ? 1 : 0,
      duration: 300,
      easing: 'easeInOutQuad',
      complete: (): void => {
        if(!show) this.hideElement = true;
      }

    })


  }

  // EFFECTS
  private readonly onViewChange: EffectRef = effect((): void => {
   
    const view: AppViews | null = this.view();

    if(view === null) return;

    if(view === AppViews.Pano) {
      this.hideElement = true;
    }
    
    this.toggleButtons(false);

  });

}
